import { NAVIGATION_CONTEXT, SessionStorageKeys } from '@consts';
import { useCallback } from 'react';

import { useSessionStorage } from './useSessionStorage';

interface NavigationContext {
  from: string;
  subscriptionId?: number;
  taskEntityId?: string;
  goalEntityId?: string;
}

/**
 * Hook for managing navigation context in sessionStorage.
 * Helps track where users came from for better UX when navigating back.
 */
const useNavigationContext = () => {
  const { readJson, writeJson, remove } = useSessionStorage();

  /**
   * Get the current navigation context from sessionStorage
   */
  const getNavigationContext = useCallback((): NavigationContext => {
    if (typeof window === 'undefined') return { from: NAVIGATION_CONTEXT.UNKNOWN };

    const lastNavigation = readJson(
      SessionStorageKeys.LAST_NAVIGATION
    ) as Partial<NavigationContext> | null;
    if (typeof lastNavigation?.from === 'string') return lastNavigation as NavigationContext;

    // Fallback to inbox
    return { from: NAVIGATION_CONTEXT.INBOX };
  }, [readJson]);

  /**
   * Set navigation context when leaving a page
   */
  const setNavigationContext = useCallback(
    (context: NavigationContext) => {
      writeJson(SessionStorageKeys.LAST_NAVIGATION, context);
    },
    [writeJson]
  );

  /**
   * Clear navigation context
   */
  const clearNavigationContext = useCallback(() => {
    remove(SessionStorageKeys.LAST_NAVIGATION);
  }, [remove]);

  return {
    getNavigationContext,
    setNavigationContext,
    clearNavigationContext
  };
};

export { useNavigationContext };
export type { NavigationContext };
